import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Card, Typography, TextField, Button, Alert, Grid,
  RadioGroup, FormControlLabel, Radio, CircularProgress, Divider, Paper, Chip,
} from '@mui/material';
import { Send, School, CheckCircle, Info, Save, HourglassTop, Login as LoginIcon, DeleteOutline } from '@mui/icons-material';
import axios from 'axios';
import ConfirmDialog from '../components/ConfirmDialog';
import FeatureCarousel from '../components/FeatureCarousel';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const EDITABLE = ['draft', 'pending', 'rejected'];

const STATUS_META = {
  draft:     { color: 'default', label: 'Draft — not yet submitted' },
  pending:   { color: 'warning', label: 'Submitted — awaiting review' },
  approved:  { color: 'info',    label: 'Approved — being set up' },
  rejected:  { color: 'error',   label: 'Rejected' },
  completed: { color: 'success', label: 'School created' },
};

const BLANK = {
  contactName: '', contactEmail: '', contactPhone: '',
  schoolName: '', address: '', city: '', county: '', postalCode: '',
  numberOfStudents: '', numberOfBoardingStudents: '',
  numberOfTeachers: '', numberOfSupportStaff: '', numberOfAdminStaff: '',
  currentSystem: '', systemDetails: '', additionalInfo: '', website: '',
};

export default function SchoolInquiryForm() {
  const navigate = useNavigate();
  const [inquiryType, setInquiryType] = useState('full');
  const [loading, setLoading] = useState(true);   // initial load of existing inquiry
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [inquiry, setInquiry] = useState(null);   // the user's current inquiry (if any)
  const [form, setForm] = useState(BLANK);
  const [confirmWithdraw, setConfirmWithdraw] = useState(false);

  const hydrate = useCallback((inq) => {
    setInquiry(inq);
    if (!inq) return;
    setInquiryType(inq.inquiryType || 'full');
    setForm({
      ...BLANK,
      ...Object.fromEntries(
        Object.keys(BLANK).map(k => [k, inq[k] == null ? '' : inq[k]])
      ),
    });
  }, []);

  // Load the user's existing inquiry (draft/pending/etc.) on mount
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/school-inquiries/mine`, { headers: hdrs() });
        hydrate(data);
      } catch {
        /* no inquiry yet — start fresh */
      } finally {
        setLoading(false);
      }
    })();
  }, [hydrate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    setError('');
  };

  const persist = async (submit) => {
    setSaving(true);
    setError('');
    setMsg('');
    const payload = { ...form, inquiryType, submit };
    try {
      const res = inquiry?._id
        ? await axios.put(`${API}/api/school-inquiries/${inquiry._id}`, payload, { headers: hdrs() })
        : await axios.post(`${API}/api/school-inquiries`, payload, { headers: hdrs() });
      hydrate(res.data);
      setMsg(submit
        ? 'Inquiry submitted! Our team will review it and get back to you.'
        : 'Draft saved. You can come back and finish it any time.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save inquiry');
    } finally {
      setSaving(false);
    }
  };

  const withdraw = async () => {
    if (!inquiry?._id) return;
    setSaving(true);
    setError('');
    setMsg('');
    try {
      await axios.delete(`${API}/api/school-inquiries/${inquiry._id}`, { headers: hdrs() });
      setConfirmWithdraw(false);
      setInquiry(null);
      setForm(BLANK);
      setInquiryType('full');
      setMsg('Inquiry withdrawn. You can start a new one below.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to withdraw inquiry');
    } finally {
      setSaving(false);
    }
  };

  const reLogin = () => {
    ['token', 'user', 'school'].forEach(k => localStorage.removeItem(k));
    navigate('/login', { state: { info: 'Your school is ready. Please sign in again to access it.' } });
  };

  if (loading) {
    return <Box textAlign="center" py={8}><CircularProgress /></Box>;
  }

  // ── Non-editable states: approved / completed → show a status card ──────────
  if (inquiry && !EDITABLE.includes(inquiry.status)) {
    const completed = inquiry.status === 'completed';
    return (
      <Container maxWidth="sm">
        <Box py={6} textAlign="center">
          {completed
            ? <CheckCircle sx={{ fontSize: 72, color: '#2e7d32', mb: 2 }} />
            : <HourglassTop sx={{ fontSize: 72, color: '#1976d2', mb: 2 }} />}
          <Typography variant="h5" fontWeight={700} mb={1}>
            {completed ? 'Your School Has Been Created!' : 'Your Request Was Approved'}
          </Typography>
          <Chip
            label={STATUS_META[inquiry.status]?.label || inquiry.status}
            color={STATUS_META[inquiry.status]?.color || 'default'}
            sx={{ mb: 2 }}
          />
          <Typography variant="body1" color="text.secondary" mb={3} lineHeight={1.8}>
            {completed
              ? `Your school "${inquiry.schoolName}" has been set up. Sign in again to access it — you'll see setup progress and be taken to your dashboard once it's ready.`
              : `Great news! Our team approved your request for "${inquiry.schoolName}" and is now preparing your system. You'll be notified by email, and can also check back here.`}
          </Typography>
          {completed && (
            <Button variant="contained" startIcon={<LoginIcon />} onClick={reLogin}
              sx={{ textTransform: 'none', fontWeight: 700 }}>
              Sign in to access your school
            </Button>
          )}
        </Box>
      </Container>
    );
  }

  // ── Editable states: no inquiry / draft / pending / rejected ────────────────
  const statusMeta = inquiry ? STATUS_META[inquiry.status] : null;

  return (
    <Container maxWidth="lg">
      <Box py={4}>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <School sx={{ fontSize: 32, color: '#1976d2' }} />
          <Typography variant="h5" fontWeight={700}>
            {inquiryType === 'full' ? 'Set Up Your School' : 'Request Demo Account'}
          </Typography>
          {statusMeta && <Chip label={statusMeta.label} color={statusMeta.color} size="small" />}
        </Box>

        {inquiry?.status === 'pending' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Your inquiry has been submitted. You can still make changes here until our team reviews it.
          </Alert>
        )}
        {inquiry?.status === 'rejected' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This inquiry was not approved{inquiry.rejectionReason ? `: ${inquiry.rejectionReason}` : '.'} You can
            update the details below and resubmit.
          </Alert>
        )}

        <Alert severity="info" sx={{ mb: 3 }}>
          We're not creating schools directly yet. Save your details as a draft any time, then submit when
          ready — our admin team will set up your system and link it to your account.
        </Alert>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 7 }}>

        {/* Inquiry Type Selection */}
        <Card sx={{ p: 3, mb: 4 }}>
          <Typography variant="subtitle1" fontWeight={700} mb={2}>
            What would you like to do?
          </Typography>
          <RadioGroup value={inquiryType} onChange={(e) => setInquiryType(e.target.value)}>
            <FormControlLabel
              value="full"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600}>Set Up My School</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Provide full details about your school for custom setup
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="demo"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600}>Request Demo Account</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Try the system with sample data first
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </Card>

        {/* Form */}
        <Card sx={{ p: 3 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {msg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMsg('')}>{msg}</Alert>}

          {/* Contact Information */}
          <Typography variant="subtitle1" fontWeight={700} mb={2}>
            Your Contact Information
          </Typography>
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth required label="Full Name" name="contactName"
                value={form.contactName} onChange={handleChange} size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth required label="Email Address" name="contactEmail" type="email"
                value={form.contactEmail} onChange={handleChange} size="small" />
            </Grid>
            {inquiryType === 'full' && (
              <Grid item xs={12} sm={6}>
                <TextField fullWidth required label="Phone Number" name="contactPhone"
                  value={form.contactPhone} onChange={handleChange} size="small"
                  placeholder="+254 7XX XXX XXX" />
              </Grid>
            )}
          </Grid>

          {inquiryType === 'full' && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="subtitle1" fontWeight={700} mb={2}>School Information</Typography>
              <Grid container spacing={2} mb={3}>
                <Grid item xs={12}>
                  <TextField fullWidth required label="School Name" name="schoolName"
                    value={form.schoolName} onChange={handleChange} size="small" />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Physical Address" name="address"
                    value={form.address} onChange={handleChange} size="small" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="City / Town" name="city"
                    value={form.city} onChange={handleChange} size="small" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="County" name="county"
                    value={form.county} onChange={handleChange} size="small" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Postal Code" name="postalCode"
                    value={form.postalCode} onChange={handleChange} size="small" />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Website (optional)" name="website"
                    value={form.website} onChange={handleChange} size="small" placeholder="https://..." />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />
              <Typography variant="subtitle1" fontWeight={700} mb={2}>School Operations</Typography>
              <Grid container spacing={2} mb={3}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Number of Students" name="numberOfStudents" type="number"
                    value={form.numberOfStudents} onChange={handleChange} size="small" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Boarding Students (if any)" name="numberOfBoardingStudents" type="number"
                    value={form.numberOfBoardingStudents} onChange={handleChange} size="small" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Number of Teachers" name="numberOfTeachers" type="number"
                    value={form.numberOfTeachers} onChange={handleChange} size="small" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Support Staff" name="numberOfSupportStaff" type="number"
                    value={form.numberOfSupportStaff} onChange={handleChange} size="small" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Admin Staff" name="numberOfAdminStaff" type="number"
                    value={form.numberOfAdminStaff} onChange={handleChange} size="small" />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />
              <Typography variant="subtitle1" fontWeight={700} mb={2}>Current System & Other Info</Typography>
              <Grid container spacing={2} mb={3}>
                <Grid item xs={12}>
                  <TextField fullWidth label="Current System (if any)" name="currentSystem"
                    value={form.currentSystem} onChange={handleChange} size="small"
                    placeholder="e.g., Paper-based, Excel, Other system" />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="System Details / Why you need to change" name="systemDetails"
                    value={form.systemDetails} onChange={handleChange} size="small" multiline rows={2} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Any other information" name="additionalInfo"
                    value={form.additionalInfo} onChange={handleChange} size="small" multiline rows={2}
                    placeholder="Anything else we should know?" />
                </Grid>
              </Grid>
            </>
          )}

          {/* Actions */}
          <Box display="flex" gap={2} mt={4} flexWrap="wrap" alignItems="center">
            <Button variant="outlined" onClick={() => navigate('/')} disabled={saving}>
              Cancel
            </Button>
            {inquiry?._id && (
              <Button color="error" startIcon={<DeleteOutline />} onClick={() => setConfirmWithdraw(true)} disabled={saving}>
                Withdraw
              </Button>
            )}
            <Box flex={1} />
            <Button variant="outlined"
              startIcon={saving ? <CircularProgress size={18} /> : <Save />}
              onClick={() => persist(false)} disabled={saving}>
              Save as Draft
            </Button>
            <Button variant="contained"
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <Send />}
              onClick={() => persist(true)} disabled={saving}>
              {inquiry?.status === 'pending' ? 'Update Submission'
                : inquiry?.status === 'rejected' ? 'Resubmit'
                : 'Submit ' + (inquiryType === 'demo' ? 'Demo Request' : 'Details')}
            </Button>
          </Box>

          {/* Info Box */}
          <Paper sx={{ p: 2, mt: 3, bgcolor: '#e3f2fd', border: '1px solid #90caf9' }}>
            <Box display="flex" gap={1}>
              <Info sx={{ color: '#1976d2', mt: 0.5 }} />
              <Box>
                <Typography variant="body2" fontWeight={600} color="#1976d2">What happens next?</Typography>
                <Typography variant="caption" display="block" mt={0.5}>
                  • Our admin team reviews your {inquiryType === 'demo' ? 'demo request' : 'submission'} within 24 hours
                </Typography>
                <Typography variant="caption" display="block">
                  • We create your school and link it to your account
                </Typography>
                <Typography variant="caption" display="block">
                  • Sign in any time to track progress — you'll go straight to your dashboard once it's ready
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 5 }}>
            <Box sx={{ position: { md: 'sticky' }, top: 24 }}>
              <FeatureCarousel />
            </Box>
          </Grid>
        </Grid>
      </Box>

      <ConfirmDialog
        open={confirmWithdraw}
        title="Withdraw this inquiry?"
        message="This removes your inquiry and you can start a fresh one. This can't be undone."
        confirmLabel="Withdraw"
        confirmColor="error"
        loading={saving}
        onConfirm={withdraw}
        onCancel={() => setConfirmWithdraw(false)}
      />
    </Container>
  );
}
